import React, { useState } from "react";
import { Button, Box, Typography, Snackbar, Alert, Tooltip, useMediaQuery } from "@mui/material";
import SmsIcon from "@mui/icons-material/Sms";
import { logDev } from "../utils/logger.js";
import { useLanguage } from "../context/LanguageContext.js";
import { normalizePhoneNumber } from "../utils/normalizePhoneNumber.js";
import { translations } from "../components/translations.js";

const BoutonEnvoyerSMSParticipantsListe = ({ contacts, nomGroupe }) => {
  const { t, locale } = useLanguage();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const isMobile = useMediaQuery("(max-width:600px)");
  const [cooldown, setCooldown] = useState(false);

  logDev("🌍 [SMS] Contexte langue", { locale, test: t("SendSurveySMS") });
  logDev("🔍 Vérification directe de la clé", {
    exists: !!translations["fr"]?.["SendSurveySMS"],
    value: translations["fr"]?.["SendSurveySMS"]
  });

  const handleClose = () => setSnackbar({ ...snackbar, open: false });

  const normalizeMultiCountry = (num, countries = ["FR", "ES", "EN", "US"]) => {
    for (const c of countries) {
      const normalized = normalizePhoneNumber(num, c);
      if (/^\+\d{7,15}$/.test(normalized)) return normalized;
    }
    return null;
  };

  const phones = contacts
    .map(c => normalizeMultiCountry(c.telephone || ""))
    .filter(Boolean);

  const hasPhones = phones.length > 0;

  logDev("📱 Numéros valides pour SMS", {
    total: phones.length,
    phones
  });

  const handleClick = () => {

    if (!hasPhones) {
      logDev("⚠️ Aucun numéro valide — SMS non envoyé");
      setSnackbar({
        open: true,
        message: t("NoValidPhone"),
        severity: "warning"
      });
      return;
    }

    if (cooldown) { 
      setSnackbar({ 
        open: true, 
        message: t("PleaseWaitBeforeSendingAgain"), 
        severity: "info" 
      }); 
      return; 
    }

    setCooldown(true);

    const lienSondage = `${window.location.origin}/sondage/${nomGroupe}`;
    const message = `${t("SMSShoppingList")} ${nomGroupe} — ${lienSondage}`;
    const encoded = encodeURIComponent(message);

    // 👉 Envoi d’un SMS PAR numéro (iPhone + Android)
    phones.forEach(num => { 
      const url = `sms:${num}?body=${encoded}`; 
      window.open(url, "_blank");
       });

    logDev("📨 Envoi SMS", {
      message,
      /*smsUrl,*/
      destinataires: phones
    });

    setSnackbar({
      open: true,
      message: t("SMSAppOpened"),
      severity: "success"
    });

    setTimeout(() => setCooldown(false), 60000); // 60 secondes

  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Tooltip
        title={hasPhones ? t("SendSMS") : t("NoValidPhone")}
        arrow
        placement="top"
      >
        <span>
          <Button
            variant="contained"
            onClick={handleClick}
            disabled={!hasPhones}
            sx={{
              backgroundColor: hasPhones ? "#FF9800 !important" : "#ccc !important",
              color: "#fff !important",
              fontWeight: "bold",
              fontSize: isMobile ? "0.5rem" : "0.7rem",
              padding: "10px 20px",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: hasPhones ? "#F57C00" : "#ccc",
                transform: hasPhones ? "scale(1.05)" : "none"
              }
            }}
          >
            <SmsIcon sx={{ mr: 0.5}} />
            {t("SendShoppingListSMS")}
          </Button>
        </span>
      </Tooltip>

      <Typography
        variant="caption"
        sx={{
          mt: 1,
          color: "#555",
          textAlign: "center"
        }}
      >
        {hasPhones
          ? `${phones.length} ${t("PhonesAvailable")}`
          : t("NoValidPhone")}
      </Typography>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BoutonEnvoyerSMSParticipantsListe;
